import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppThunkDispatch } from 'state/store';
import {
  selectIsSidePanelFiltersVisible,
  toggleSidePanelFilters,
  fetchProjects,
} from 'state/ProjectsList/projectsListSlice';
import {
  selectAppliedFilters,
  setFilter,
  upsertFiltersSettings,
} from 'state/ProjectsList/filters/filtersSlice';
import { ProjectsListFilters } from 'types/store/projectsList';
import { PersistentFilterTypeKeys } from 'utils/types/filters';

interface UseProjectsListSidePanelFiltersParams {
  filterTypeKey: PersistentFilterTypeKeys;
  isConsolidatedView?: boolean;
}

interface UseProjectsListSidePanelFiltersReturn {
  isSidePanelFiltersVisible: boolean;
  toggleSidePanelFiltersVisibility: () => void;
  handleUpdateFilters: (filters: ProjectsListFilters) => void;
  applyFilters: (filters: ProjectsListFilters) => void;
  clearFilters: () => void;
  selectedFiltersCount: number;
  selectedFilters: ProjectsListFilters;
  appliedFilters: ProjectsListFilters;
}

const useProjectsListSidePanelFilters: (
  params: UseProjectsListSidePanelFiltersParams
) => UseProjectsListSidePanelFiltersReturn = ({
  filterTypeKey,
  isConsolidatedView = false,
}) => {
  const dispatch = useAppThunkDispatch();
  const appliedFilters = useSelector(selectAppliedFilters);
  const previouslyAppliedFilters = useRef<ProjectsListFilters | undefined>(
    undefined
  );
  const [currentFilters, setCurrentFilters] = useState<ProjectsListFilters>({});

  const isSidePanelFiltersVisible = useSelector(
    selectIsSidePanelFiltersVisible
  );

  const handleUpdateFilters = useCallback((filters: ProjectsListFilters) => {
    setCurrentFilters((prevFilters) => ({
      ...prevFilters,
      ...filters,
    }));
  }, []);

  const toggleSidePanelFiltersVisibility = useCallback(() => {
    dispatch(toggleSidePanelFilters());
  }, [dispatch]);

  const clearFilters = useCallback(async () => {
    const filters: ProjectsListFilters = {};
    setCurrentFilters(filters);
    dispatch(setFilter(filters));
    dispatch(upsertFiltersSettings({ filterTypeKey }));
    dispatch(fetchProjects({ includeTotalCount: true, isConsolidatedView }));
  }, [dispatch, isConsolidatedView, filterTypeKey]);

  const applyFilters = useCallback(
    async (filters: ProjectsListFilters) => {
      dispatch(setFilter(filters));
      dispatch(upsertFiltersSettings({ filterTypeKey }));
      dispatch(
        fetchProjects({
          includeTotalCount: true,
          isConsolidatedView,
        })
      );
    },
    [dispatch, isConsolidatedView, filterTypeKey]
  );

  const shouldUpdateCurrentFilters = useMemo(() => {
    const oldFilters = previouslyAppliedFilters.current ?? {};
    const newFilters = appliedFilters ?? {};
    const oldFiltersKeys = Object.keys(oldFilters);
    const newFiltersKeys = Object.keys(newFilters);
    if (oldFiltersKeys.length !== newFiltersKeys.length) {
      return true;
    }
    let shouldUpdate = false;
    for (const key of newFiltersKeys) {
      const filterKey = key as keyof ProjectsListFilters;
      if (!oldFilters[filterKey]) {
        shouldUpdate = true;
        break;
      }
      const oldFilterValue = oldFilters[filterKey];
      const newFilterValue = newFilters[filterKey];
      if (Array.isArray(oldFilterValue) && Array.isArray(newFilterValue)) {
        shouldUpdate = oldFilterValue.length !== newFilterValue.length;
        if (shouldUpdate) {
          break;
        }
      }
    }
    return shouldUpdate;
  }, [appliedFilters]);

  const selectedFiltersCount = useMemo(() => {
    return Object.keys(currentFilters).filter((key: string) => {
      const filterKey = key as keyof ProjectsListFilters;
      const currentFilterValue = currentFilters[filterKey];
      if (Array.isArray(currentFilterValue)) {
        return currentFilterValue.length > 0;
      }
      return currentFilterValue !== undefined;
    }).length;
  }, [currentFilters]);

  useEffect(() => {
    if (shouldUpdateCurrentFilters) {
      previouslyAppliedFilters.current = appliedFilters;
      setCurrentFilters(appliedFilters);
    }
  }, [appliedFilters, shouldUpdateCurrentFilters]);

  return useMemo(
    () => ({
      isSidePanelFiltersVisible,
      toggleSidePanelFiltersVisibility,
      handleUpdateFilters,
      clearFilters,
      applyFilters,
      selectedFiltersCount,
      selectedFilters: currentFilters,
      appliedFilters,
    }),
    [
      isSidePanelFiltersVisible,
      toggleSidePanelFiltersVisibility,
      handleUpdateFilters,
      clearFilters,
      applyFilters,
      appliedFilters,
      currentFilters,
      selectedFiltersCount,
    ]
  );
};

export default useProjectsListSidePanelFilters;
